import React, { useEffect, useState } from 'react'
import Lottie from 'lottie-react'
import CountryStep from './CountryStep'
import AmountStep from './AmountStep'
import ProviderStep from './ProviderStep'
import CheckoutStep from './CheckoutStep'
import loadingAnimation from '../../../../assets/lottie/loading.json'
import { moneyFromMinor } from '../../../utils/UnitConversion.utils'
import { useDepositWithdrawModal } from '../DepositWithdrawModalContext'
import api from '../../../../api/axios'
import { Check } from 'lucide-react'
import { SUPPORTED_COUNTRIES } from '../countries'
import { getCurrencySymbol } from '../DepositWithdrawModal'

export type CountryProviderFeeDTO = {
    id: string
    countryCode: string
    providerFeeType: string
    inputCurrency: string
    outputCurrency: string
    fixedMinor: number
    feeBps: number
    fxFeeBps: number
    minAmountMinor: number
    maxAmountMinor: number
    kycRequired: boolean
    isActive: boolean
}

type Props = {
    presetAmounts: number[]
    currency: string
    symbol: string
    rate: number
    eurRateScaled: number
    convertFromUSD: (v: number) => number
    formatAmountWithoutCurrency: (v: number) => string
    provider: string | null
    setProvider: (v: string | null) => void
    selectedCountry: string | null
    setSelectedCountry: React.Dispatch<React.SetStateAction<string | null>>
    closeModal: () => void
    kyc: string
}

const getProviderKey = (fee: CountryProviderFeeDTO): string => {
    return fee.providerFeeType || (fee as unknown as { name?: string }).name || ''
}

const Deposit: React.FC<Props> = ({
                                      presetAmounts,
                                      currency,
                                      symbol,
                                      rate,
                                      eurRateScaled,
                                      convertFromUSD,
                                      formatAmountWithoutCurrency,
                                      provider,
                                      setProvider,
                                      selectedCountry,
                                      setSelectedCountry,
                                      closeModal,
                                      kyc,
                                  }) => {
    const { initialAmountUsd } = useDepositWithdrawModal()
    const [targetAmountInUsd, setTargetAmountInUsd] = useState(0)
    const [amountInputInUserCurrency, setAmountInputInUserCurrency] = useState('')
    const [availableFees, setAvailableFees] = useState<CountryProviderFeeDTO[]>([])
    const [, setHasUserEditedAmount] = useState(false)
    const [isDetectingCountry, setIsDetectingCountry] = useState(true)
    const [isMethodsLoading, setIsMethodsLoading] = useState(false)

    // Resolve active country object and its specific currency fallback to Redux currency
    const activeCountryObj = SUPPORTED_COUNTRIES.find(c => c.enumName === selectedCountry || c.code.toLowerCase() === selectedCountry?.toLowerCase())
    const selectedCountryCurrency = activeCountryObj?.currency || currency
    const selectedCountrySymbol = getCurrencySymbol(selectedCountryCurrency)

    // Convert USD amount to country specific currency using country rate
    const convertCountryFromUSD = (usdAmount: number) => usdAmount * rate

    // Detect user country location via IP on component mount
    useEffect(() => {
        if (selectedCountry) {
            setIsDetectingCountry(false)
            return
        }

        const fetchUserCountry = async () => {
            setIsDetectingCountry(true)
            try {
                const response = await fetch('https://ipapi.co/json/')
                const data = await response.json()
                if (data && data.country_code) {
                    const code = data.country_code.toLowerCase()
                    const found = SUPPORTED_COUNTRIES.find(c => c.code === code)
                    setSelectedCountry(found ? found.enumName : 'GERMANY')
                } else {
                    setSelectedCountry('GERMANY')
                }
            } catch {
                setSelectedCountry('GERMANY')
            } finally {
                setIsDetectingCountry(false)
            }
        }

        fetchUserCountry()
    }, [selectedCountry, setSelectedCountry])

    // Fetch payment options dynamic per country enum
    useEffect(() => {
        if (!selectedCountry) return

        const fetchCountryMethods = async () => {
            setIsMethodsLoading(true)
            try {
                const response = await api.get<CountryProviderFeeDTO[]>(`/payment-methods/country/${selectedCountry}`)
                setAvailableFees(response.data)
                if (response.data.length > 0) {
                    setProvider(getProviderKey(response.data[0]))
                } else {
                    setProvider(null)
                }
            } catch {
                setAvailableFees([])
                setProvider(null)
            } finally {
                setIsMethodsLoading(false)
            }
        }

        fetchCountryMethods()
    }, [selectedCountry, setProvider])

    useEffect(() => {
        if (initialAmountUsd && availableFees.length > 0) {
            const minAcrossProviders = Math.min(...availableFees.map(f => moneyFromMinor(f.minAmountMinor)))
            const finalAmount = Math.max(initialAmountUsd, minAcrossProviders)

            setTargetAmountInUsd(finalAmount)
            setAmountInputInUserCurrency(formatAmountWithoutCurrency(convertCountryFromUSD(finalAmount)))
        }
    }, [initialAmountUsd, availableFees, convertCountryFromUSD, formatAmountWithoutCurrency])

    // Match provider key against providerFeeType or fallback name field
    const selectedFee = availableFees.find(f => getProviderKey(f) === provider) || null
    // Convert min/max limits from backend USD minor units to target user currency
    const min = selectedFee ? convertCountryFromUSD(moneyFromMinor(selectedFee.minAmountMinor)) : undefined
    const max = selectedFee ? convertCountryFromUSD(moneyFromMinor(selectedFee.maxAmountMinor)) : undefined

    const isCountryValid = selectedCountry !== null
    const isProviderValid = provider !== null && isCountryValid

    // Compare user target amount converted to user currency against min/max limits
    const amountInUserCurrency = convertCountryFromUSD(targetAmountInUsd)
    const isAmountValid =
        isProviderValid &&
        targetAmountInUsd > 0 &&
        (min === undefined || amountInUserCurrency >= min) &&
        (max === undefined || amountInUserCurrency <= max)

    const isInitialLoading = isDetectingCountry || isMethodsLoading

    if (isInitialLoading) {
        return (
            <div className="flex-1 min-h-[70vh] flex items-center justify-center w-full">
                <Lottie animationData={loadingAnimation} loop />
            </div>
        )
    }

    return (
        <div className="flex-1 relative pr-1">
            <div className="flex flex-col gap-3.5">

                {/* Step 1: Country & Payment Method */}
                <div className="rounded-xl border border-neutral-200 dark:border-neutral-800/80 bg-neutral-50/50 dark:bg-neutral-900/60 p-4 relative">
                    <div className="flex items-center justify-between mb-3.5 border-b border-neutral-200/60 dark:border-neutral-800/60 pb-3">
                        <div className="flex items-center gap-2">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                                isProviderValid ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
                            }`}>
                                {isProviderValid ? <Check size={12} /> : '1'}
                            </div>
                            <span className="text-xs font-bold text-neutral-900 dark:text-white">
                                Payment Method
                            </span>
                        </div>

                        <div className="w-44">
                            <CountryStep
                                selectedCountryEnum={selectedCountry}
                                setSelectedCountryEnum={setSelectedCountry}
                            />
                        </div>
                    </div>

                    <ProviderStep
                        provider={provider}
                        setProvider={setProvider}
                        depositFees={availableFees}
                        currencySymbol={selectedCountrySymbol}
                        convertFromUSD={convertCountryFromUSD}
                    />
                </div>

                {/* Step 2: Deposit Amount */}
                <div className={`rounded-xl border transition-all duration-200 p-4 ${
                    isProviderValid
                        ? 'border-neutral-200 dark:border-neutral-800/80 bg-neutral-50/50 dark:bg-neutral-900/60'
                        : 'border-neutral-200/40 dark:border-neutral-800/30 bg-neutral-50/20 dark:bg-neutral-950/20 opacity-40 pointer-events-none'
                }`}>
                    <div className="flex items-center gap-2 mb-3.5 border-b border-neutral-200/60 dark:border-neutral-800/60 pb-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            isAmountValid
                                ? 'bg-emerald-500 text-white'
                                : isProviderValid
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500'
                        }`}>
                            {isAmountValid ? <Check size={12} /> : '2'}
                        </div>
                        <span className="text-xs font-bold text-neutral-900 dark:text-white">
                            Deposit Amount
                        </span>
                    </div>

                    <AmountStep
                        amountInputInUserCurrency={amountInputInUserCurrency}
                        setAmountInputInUserCurrency={v => {
                            setHasUserEditedAmount(true)
                            setAmountInputInUserCurrency(v)
                        }}
                        targetAmountInUsd={targetAmountInUsd}
                        setTargetAmountInUsd={v => {
                            setHasUserEditedAmount(true)
                            setTargetAmountInUsd(v)
                        }}
                        presetAmounts={presetAmounts}
                        currency={selectedCountryCurrency}
                        currencySymbol={selectedCountrySymbol}
                        convertFromUSD={convertCountryFromUSD}
                        formatAmountWithoutCurrency={formatAmountWithoutCurrency}
                        minAmount={min}
                        maxAmount={max}
                    />
                </div>

                {/* Step 3: Summary & Checkout */}
                <div className={`rounded-xl border transition-all duration-200 p-4 ${
                    isAmountValid
                        ? 'border-neutral-200 dark:border-neutral-800/80 bg-neutral-50/50 dark:bg-neutral-900/60'
                        : 'border-neutral-200/40 dark:border-neutral-800/30 bg-neutral-50/20 dark:bg-neutral-950/20 opacity-40 pointer-events-none'
                }`}>
                    <div className="flex items-center gap-2 mb-3.5 border-b border-neutral-200/60 dark:border-neutral-800/60 pb-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            isAmountValid ? 'bg-blue-500 text-white' : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500'
                        }`}>
                            3
                        </div>
                        <span className="text-xs font-bold text-neutral-900 dark:text-white">
                            Summary & Checkout
                        </span>
                    </div>

                    <CheckoutStep
                        provider={provider}
                        country={selectedCountry}
                        selectedCountryEnum={selectedCountry}
                        setSelectedCountryEnum={setSelectedCountry}
                        amountInputInUserCurrency={amountInputInUserCurrency}
                        targetAmountInUsd={targetAmountInUsd}
                        setModalOpen={closeModal}
                        kyc={kyc}
                        currency={selectedCountryCurrency}
                        currencyRate={rate}
                        eurRateScaled={eurRateScaled}
                        currencySymbol={selectedCountrySymbol}
                        convertFromUSD={convertCountryFromUSD}
                        depositFees={availableFees}
                        minAmount={min}
                        maxAmount={max}
                    />
                </div>

            </div>
        </div>
    )
}

export default Deposit